import toast from "react-hot-toast";

const BASE = "/api/v1/ads";

class AdsAPIs {
  static async createAdtype({ axiosPrivate, payload }) {
    try {
      const res = await axiosPrivate.post(`${BASE}/adtypes/`, payload);
      return res?.data?.data ?? res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async listAdtypes({ axiosPrivate, limit = 20, offset = 0 }) {
    try {
      const params = new URLSearchParams({
        ...(limit != null && { limit }),
        ...(offset != null && { offset }),
      });
      const q = params.toString();
      const url = q ? `${BASE}/adtypes/?${q}` : `${BASE}/adtypes/`;
      const res = await axiosPrivate.get(url);
      const list = res?.data?.data ?? res?.data ?? [];
      const count = res?.data?.count ?? list?.length ?? 0;
      return { data: Array.isArray(list) ? list : [], count };
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      return { data: [], count: 0 };
    }
  }

  static async getAdJourneyStats({
    axiosPrivate,
    date_from,
    date_to,
    delivered_date_from,
    delivered_date_to,
    requested_for_agency_id,
    requested_by_id,
  }) {
    try {
      const params = new URLSearchParams({
        ...(date_from && { date_from }),
        ...(date_to && { date_to }),
        ...(delivered_date_from && { delivered_date_from }),
        ...(delivered_date_to && { delivered_date_to }),
        ...(requested_for_agency_id && { requested_for_agency_id }),
        ...(requested_by_id && { requested_by_id }),
      });
      const q = params.toString();
      const url = q ? `${BASE}/journey/?${q}` : `${BASE}/journey/`;
      const res = await axiosPrivate.get(url);
      return res?.data?.data ?? res?.data ?? null;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      return null;
    }
  }

  static async listAdRequests({
    axiosPrivate,
    status,
    priority,
    qc_status,
    requested_for_agency_id,
    requested_by_id,
    requested_via,
    date_from,
    date_to,
    launched_from,
    launched_to,
    app,
    language,
    category,
    search,
    sprint_plan_assigned,
    limit = 20,
    offset = 0,
  }) {
    try {
      const params = new URLSearchParams({
        ...(status && { status }),
        ...(requested_via && { requested_via }),
        ...(priority && { priority }),
        ...(qc_status && { qc_status }),
        ...(requested_for_agency_id && { requested_for_agency_id }),
        ...(requested_by_id && { requested_by_id }),
        ...(date_from && { date_from }),
        ...(date_to && { date_to }),
        ...(launched_from && { launched_from }),
        ...(launched_to && { launched_to }),
        ...(app && { app }),
        ...(language && { language }),
        ...(category && { category }),
        ...(search && { search }),
        ...(sprint_plan_assigned != null && {
          sprint_plan_assigned: String(sprint_plan_assigned),
        }),
        ...(limit != null && { limit }),
        ...(offset != null && { offset }),
      });
      const q = params.toString();
      const url = q ? `${BASE}/ad-requests/?${q}` : `${BASE}/ad-requests/`;
      const res = await axiosPrivate.get(url);
      const list = res?.data?.data ?? res?.data ?? [];
      const count = res?.data?.count ?? list?.length ?? 0;
      return { data: Array.isArray(list) ? list : [], count };
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      return { data: [], count: 0 };
    }
  }

  static async createAdRequest({ axiosPrivate, payload }) {
    try {
      const res = await axiosPrivate.post(`${BASE}/ad-requests/`, payload);
      return res?.data?.data ?? res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  // Requests the agency creates and delivers in one submit; all rows or none. Callers show the
  // error, since row numbers in it need mapping back to the grid.
  static async createDirectAdRequests({ axiosPrivate, payload }) {
    const res = await axiosPrivate.post(`${BASE}/ad-requests/direct/`, payload);
    return res?.data;
  }

  static async bulkCreateAdRequests({ axiosPrivate, requests }) {
    try {
      const res = await axiosPrivate.post(`${BASE}/ad-requests/bulk/`, {
        requests,
      });
      const data = res?.data?.data ?? res?.data ?? [];
      const count = res?.data?.count ?? (Array.isArray(data) ? data.length : 0);
      return { data: Array.isArray(data) ? data : [], count };
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async getAdRequest({ axiosPrivate, id }) {
    try {
      const res = await axiosPrivate.get(`${BASE}/ad-requests/${id}/`);
      return res?.data?.data ?? res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      return null;
    }
  }

  static async updateAdRequest({ axiosPrivate, id, payload }) {
    try {
      const res = await axiosPrivate.patch(
        `${BASE}/ad-requests/${id}/`,
        payload
      );
      return res?.data?.data ?? res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async handleUpdateAutoLaunch({ axiosPrivate, id, auto_launch }) {
    try {
      const res = await axiosPrivate.patch(
        `${BASE}/ad-requests/${id}/auto-launch/`,
        { auto_launch }
      );
      return res?.data?.data ?? res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async handleUpdateLaunchDate({ axiosPrivate, id, launch_date }) {
    try {
      const res = await axiosPrivate.patch(
        `${BASE}/ad-requests/${id}/launch-date/`,
        { launch_date }
      );
      return res?.data?.data ?? res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async handleBulkUpdateLaunchDate({ axiosPrivate, ids, launch_date }) {
    try {
      const res = await axiosPrivate.patch(
        `${BASE}/ad-requests/launch-date/bulk/`,
        { ids, launch_date }
      );
      return res?.data?.data ?? res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async deleteAdRequestsBulk({ axiosPrivate, ids, date_from, date_to }) {
    try {
      const payload = ids != null ? { ids } : { date_from, date_to };
      const res = await axiosPrivate.post(
        `${BASE}/ad-requests/bulk-delete/`,
        payload
      );
      return res?.data?.deleted ?? 0;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async listSprints({ axiosPrivate, limit = 20, offset = 0 }) {
    try {
      const params = new URLSearchParams({
        ...(limit != null && { limit }),
        ...(offset != null && { offset }),
      });
      const q = params.toString();
      const url = q ? `${BASE}/sprints/?${q}` : `${BASE}/sprints/`;
      const res = await axiosPrivate.get(url);
      const list = res?.data?.data ?? res?.data ?? [];
      const count = res?.data?.count ?? list?.length ?? 0;
      return { data: Array.isArray(list) ? list : [], count };
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      return { data: [], count: 0 };
    }
  }

  static async createSprint({ axiosPrivate, payload }) {
    try {
      const res = await axiosPrivate.post(`${BASE}/sprints/`, payload);
      return res?.data?.data ?? res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async listDeliveries({
    axiosPrivate,
    delivered_by_agency_id,
    requested_by_id,
    requested_via,
    date_from,
    date_to,
    search,
    finalized_rejected_only,
    moderation_status_filter,
    qc_status,
    include_pending,
    limit = 20,
    offset = 0,
  }) {
    try {
      const params = new URLSearchParams({
        ...(delivered_by_agency_id && { delivered_by_agency_id }),
        ...(requested_via && { requested_via }),
        ...(requested_by_id && { requested_by_id }),
        ...(date_from && { date_from }),
        ...(date_to && { date_to }),
        ...(search && { search }),
        ...(finalized_rejected_only && { finalized_rejected_only: "true" }),
        ...(moderation_status_filter && { moderation_status_filter }),
        ...(qc_status && { qc_status }),
        ...(include_pending && { include_pending: "true" }),
        ...(limit != null && { limit }),
        ...(offset != null && { offset }),
      });
      const q = params.toString();
      const url = q ? `${BASE}/deliveries/?${q}` : `${BASE}/deliveries/`;
      const res = await axiosPrivate.get(url);
      const list = res?.data?.data ?? res?.data ?? [];
      const count = res?.data?.count ?? list?.length ?? 0;
      const pendingCount = res?.data?.pending_count ?? 0;
      const deliveredCount = res?.data?.delivered_count ?? count;
      return {
        data: Array.isArray(list) ? list : [],
        count,
        pendingCount,
        deliveredCount,
      };
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      return { data: [], count: 0, pendingCount: 0, deliveredCount: 0 };
    }
  }

  // approve is request-level and takes nothing; request_changes carries one note
  // per delivery, and those notes are the selection — a delivery with no note is
  // not sent back.
  static async qcRequestAction({ axiosPrivate, requestId, action, items }) {
    const res = await axiosPrivate.post(
      `${BASE}/ad-requests/${requestId}/qc/`,
      {
        action,
        ...(items?.length ? { items } : {}),
      }
    );
    return res?.data?.data ?? null;
  }

  // Save one delivery's note-in-progress. Empty feedback deletes the draft,
  // which is how a reviewer takes a video back out of the round.
  static async qcSaveDraft({ axiosPrivate, requestId, deliveryId, feedback }) {
    const res = await axiosPrivate.post(
      `${BASE}/ad-requests/${requestId}/qc/draft/`,
      { delivery_id: deliveryId, feedback }
    );
    return res?.data?.data ?? null;
  }

  static async retryS3Migration({ axiosPrivate, id }) {
    try {
      const res = await axiosPrivate.post(`${BASE}/deliveries/${id}/retry-s3/`);
      return res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async getDelivery({ axiosPrivate, id }) {
    try {
      const res = await axiosPrivate.get(`${BASE}/deliveries/${id}/`);
      return res?.data?.data ?? res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      return null;
    }
  }

  static async createDelivery({ axiosPrivate, payload }) {
    try {
      const res = await axiosPrivate.post(`${BASE}/deliveries/`, payload);
      return res?.data?.data ?? res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async createDeliveriesBulk({ axiosPrivate, deliveries }) {
    try {
      const res = await axiosPrivate.post(`${BASE}/deliveries/`, {
        deliveries,
      });
      const data = res?.data?.data ?? res?.data ?? [];
      const count = res?.data?.count ?? (Array.isArray(data) ? data.length : 0);
      return { data: Array.isArray(data) ? data : [], count };
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async listModerations({
    axiosPrivate,
    status,
    delivery_date_from,
    delivery_date_to,
    iteration_gte,
    iteration_gt,
    iteration_lt,
    search,
    app_name,
    agency_id,
    account,
    language,
    requested_by_id,
    include_counts,
    limit = 20,
    offset = 0,
  }) {
    try {
      const params = new URLSearchParams({
        ...(status && { status }),
        ...(delivery_date_from && { delivery_date_from }),
        ...(delivery_date_to && { delivery_date_to }),
        ...(iteration_gte != null && iteration_gte !== "" && { iteration_gte }),
        ...(iteration_gt != null && iteration_gt !== "" && { iteration_gt }),
        ...(iteration_lt != null && iteration_lt !== "" && { iteration_lt }),
        ...(search && { search }),
        ...(app_name && { app_name }),
        ...(agency_id != null && agency_id !== "" && { agency_id }),
        ...(account && { account }),
        ...(language && { language }),
        ...(requested_by_id && { requested_by_id }),
        ...(include_counts && { include_counts: "true" }),
        ...(limit != null && { limit }),
        ...(offset != null && { offset }),
      });
      const q = params.toString();
      const url = q ? `${BASE}/moderations/?${q}` : `${BASE}/moderations/`;
      const res = await axiosPrivate.get(url);
      const list = res?.data?.data ?? res?.data ?? [];
      const count = res?.data?.count ?? list?.length ?? 0;
      const counts = res?.data?.counts;
      return {
        data: Array.isArray(list) ? list : [],
        count,
        ...(counts ? { counts } : {}),
      };
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      return { data: [], count: 0 };
    }
  }

  static async getModerationContext({ axiosPrivate, ad_id, ad_moderate_id }) {
    try {
      if (ad_id == null || ad_id === "" || Number(ad_id) === -7)
        return {
          data: {
            ad: null,
            ad_deliveries: [],
            moderation_history: [],
            current_user_has_activity: false,
            active_moderation: null,
            video_preview_url: "",
          },
        };
      const params = new URLSearchParams({
        ad_id: Number(ad_id),
        ...(ad_moderate_id != null &&
          ad_moderate_id !== "" && { ad_moderate_id: Number(ad_moderate_id) }),
      });
      const res = await axiosPrivate.get(
        `${BASE}/moderations/context/?${params.toString()}`
      );
      return (
        res?.data ?? {
          data: {
            ad: null,
            ad_deliveries: [],
            moderation_history: [],
            current_user_has_activity: false,
            active_moderation: null,
            video_preview_url: "",
          },
        }
      );
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      return {
        data: {
          ad: null,
          ad_deliveries: [],
          moderation_history: [],
          current_user_has_activity: false,
          active_moderation: null,
          video_preview_url: "",
        },
      };
    }
  }

  static async addModerationContextComment({
    axiosPrivate,
    ad_id,
    ad_moderate_id,
    notes,
  }) {
    try {
      const res = await axiosPrivate.post(
        `${BASE}/moderations/context/comment/`,
        {
          ad_id: Number(ad_id),
          ...(ad_moderate_id != null && {
            ad_moderate_id: Number(ad_moderate_id),
          }),
          notes: notes || "",
        }
      );
      return res?.data?.data ?? res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async updateModeration({ axiosPrivate, id, payload }) {
    try {
      const res = await axiosPrivate.patch(
        `${BASE}/moderations/${id}/`,
        payload
      );
      return res?.data?.data ?? res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async overrideModeration({ axiosPrivate, id, payload }) {
    try {
      const res = await axiosPrivate.post(`${BASE}/moderations/override/`, {
        ad_moderate_id: Number(id),
        ...payload,
      });
      return res?.data?.data ?? res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async uploadFile({ axiosPrivate, file }) {
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axiosPrivate.post(`${BASE}/upload/`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = res?.data?.url ?? res?.data?.data?.url ?? null;
      return url;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async listAdTypesForAgency({ axiosPrivate, agency_id }) {
    try {
      if (agency_id == null) return { data: [], count: 0 };
      const params = new URLSearchParams({ agency_id });
      const res = await axiosPrivate.get(
        `${BASE}/ad-types/?${params.toString()}`
      );
      const list = res?.data?.data ?? res?.data ?? [];
      const count = res?.data?.count ?? list?.length ?? 0;
      return { data: Array.isArray(list) ? list : [], count };
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      return { data: [], count: 0 };
    }
  }

  static async listRateCards({
    axiosPrivate,
    agency_id,
    adtype_id,
    limit = 20,
    offset = 0,
  }) {
    try {
      const params = new URLSearchParams({
        ...(agency_id != null && { agency_id }),
        ...(adtype_id != null && { adtype_id }),
        ...(limit != null && { limit }),
        ...(offset != null && { offset }),
      });
      const q = params.toString();
      const url = q ? `${BASE}/rate-cards/?${q}` : `${BASE}/rate-cards/`;
      const res = await axiosPrivate.get(url);
      const list = res?.data?.data ?? res?.data ?? [];
      const count = res?.data?.count ?? list?.length ?? 0;
      return { data: Array.isArray(list) ? list : [], count };
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      return { data: [], count: 0 };
    }
  }

  static async createRateCard({ axiosPrivate, payload }) {
    try {
      const res = await axiosPrivate.post(`${BASE}/rate-cards/`, payload);
      return res?.data?.data ?? res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async updateRateCard({ axiosPrivate, id, payload }) {
    try {
      const res = await axiosPrivate.patch(
        `${BASE}/rate-cards/${id}/`,
        payload
      );
      return res?.data?.data ?? res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async deleteRateCard({ axiosPrivate, id }) {
    try {
      await axiosPrivate.delete(`${BASE}/rate-cards/${id}/`);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async getHookSummary({ axiosPrivate, date_from, date_to, agency_id }) {
    try {
      const params = new URLSearchParams({
        ...(date_from && { date_from }),
        ...(date_to && { date_to }),
        ...(agency_id != null && { agency_id }),
      });
      const res = await axiosPrivate.get(
        `${BASE}/rate-cards/hook-summary/?${params}`
      );
      return res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async listRecco({
    axiosPrivate,
    agency_id,
    delivery_date_from,
    delivery_date_to,
    limit = 20,
    offset = 0,
  }) {
    try {
      const params = new URLSearchParams({
        ...(agency_id != null && { agency_id }),
        ...(delivery_date_from && { delivery_date_from }),
        ...(delivery_date_to && { delivery_date_to }),
        ...(limit != null && { limit }),
        ...(offset != null && { offset }),
      });
      const q = params.toString();
      const url = q ? `${BASE}/recco/?${q}` : `${BASE}/recco/`;
      const res = await axiosPrivate.get(url);
      const list = res?.data?.data ?? res?.data ?? [];
      const count = res?.data?.count ?? list?.length ?? 0;
      return { data: Array.isArray(list) ? list : [], count };
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      return { data: [], count: 0 };
    }
  }

  static async getReccoDetails({
    axiosPrivate,
    agency_id,
    adtype_id,
    delivery_date_from,
    delivery_date_to,
    limit = 20,
    offset = 0,
  }) {
    try {
      const params = new URLSearchParams({
        ...(agency_id != null && { agency_id }),
        ...(adtype_id != null && { adtype_id }),
        ...(delivery_date_from && { delivery_date_from }),
        ...(delivery_date_to && { delivery_date_to }),
        ...(limit != null && { limit }),
        ...(offset != null && { offset }),
      });
      const q = params.toString();
      const url = q ? `${BASE}/recco/details/?${q}` : `${BASE}/recco/details/`;
      const res = await axiosPrivate.get(url);
      const list = res?.data?.data ?? res?.data ?? [];
      const count = res?.data?.count ?? list?.length ?? 0;
      return { data: Array.isArray(list) ? list : [], count };
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      return { data: [], count: 0 };
    }
  }

  static async listLaunches({
    axiosPrivate,
    status,
    journey_status,
    launch_only,
    group_by,
    ad_id,
    date_from,
    date_to,
    requested_date_from,
    requested_date_to,
    delivered_date_from,
    delivered_date_to,
    launch_date_from,
    launch_date_to,
    search,
    track,
    category,
    requested_by_id,
    limit = 20,
    offset = 0,
  }) {
    try {
      const params = new URLSearchParams({
        ...(status && { status }),
        ...(journey_status && { journey_status }),
        ...(launch_only && { launch_only: "true" }),
        ...(group_by && { group_by }),
        ...(ad_id != null && { ad_id }),
        ...(date_from && { date_from }),
        ...(date_to && { date_to }),
        ...(requested_date_from && { requested_date_from }),
        ...(requested_date_to && { requested_date_to }),
        ...(delivered_date_from && { delivered_date_from }),
        ...(delivered_date_to && { delivered_date_to }),
        ...(launch_date_from && { launch_date_from }),
        ...(launch_date_to && { launch_date_to }),
        ...(search && { search }),
        ...(track && { track }),
        ...(category && { category }),
        ...(requested_by_id && { requested_by_id }),
        ...(limit != null && { limit }),
        ...(offset != null && { offset }),
      });
      const q = params.toString();
      const url = q ? `${BASE}/launch/?${q}` : `${BASE}/launch/`;
      const res = await axiosPrivate.get(url);
      const list = res?.data?.data ?? res?.data ?? [];
      const count = res?.data?.count ?? list?.length ?? 0;
      const statusCounts = res?.data?.status_counts ?? {};
      const hookCount = res?.data?.hook_count ?? count;
      return {
        data: Array.isArray(list) ? list : [],
        count,
        hookCount,
        statusCounts,
      };
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      return { data: [], count: 0, hookCount: 0, statusCounts: {} };
    }
  }

  static async listLaunchFacets({ axiosPrivate }) {
    try {
      const res = await axiosPrivate.get(`${BASE}/launch/facets/`);
      const payload = res?.data?.data ?? res?.data ?? {};
      return {
        tracks: Array.isArray(payload?.tracks) ? payload.tracks : [],
        categories: Array.isArray(payload?.categories)
          ? payload.categories
          : [],
      };
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      return { tracks: [], categories: [] };
    }
  }

  static async launchAd({ axiosPrivate, ad_moderate_id }) {
    try {
      const res = await axiosPrivate.post(`${BASE}/launch/`, {
        ad_moderate_id,
      });
      return res?.data?.data ?? res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async launchAdsBulk({
    axiosPrivate,
    ad_moderate_ids,
    meta_ad_account_id,
    meta_campaign_id,
    for_app_name,
    track,
    category,
    scheduled_at,
    adset_daily_budget,
    daily_min_spend_target,
    daily_spend_cap,
    start_time,
    end_time,
  }) {
    try {
      const payload = {
        ad_moderate_ids,
        ...(meta_ad_account_id && { meta_ad_account_id }),
        ...(meta_campaign_id && { meta_campaign_id }),
        ...(for_app_name && { for_app_name }),
        ...(track && { track }),
        ...(category && { category }),
        ...(scheduled_at && { scheduled_at }),
        ...(adset_daily_budget != null && { adset_daily_budget }),
        ...(daily_min_spend_target != null && { daily_min_spend_target }),
        ...(daily_spend_cap != null && { daily_spend_cap }),
        ...(start_time && { start_time }),
        ...(end_time && { end_time }),
      };
      const res = await axiosPrivate.post(`${BASE}/launch/bulk/`, payload);
      return res?.data ?? {};
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async cancelLaunch({ axiosPrivate, id }) {
    try {
      const res = await axiosPrivate.post(`${BASE}/launch/${id}/cancel/`);
      return res?.data?.data ?? res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async rescheduleLaunch({ axiosPrivate, id, scheduled_at }) {
    try {
      const res = await axiosPrivate.post(`${BASE}/launch/${id}/reschedule/`, {
        scheduled_at,
      });
      return res?.data?.data ?? res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async refreshLaunchStatus({ axiosPrivate, id }) {
    try {
      const res = await axiosPrivate.post(
        `${BASE}/launch/${id}/refresh-status/`
      );
      return res?.data?.data ?? res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async markLaunchLive({ axiosPrivate, ad_moderate_id }) {
    try {
      const res = await axiosPrivate.post(`${BASE}/launch/mark-live/`, {
        ad_moderate_id,
      });
      return res?.data?.data ?? res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async syncLaunchLive({
    axiosPrivate,
    since_days,
    mode,
    ad_account_id,
  }) {
    try {
      const res = await axiosPrivate.post(`${BASE}/launch/sync-live/`, {
        ...(since_days && { since_days }),
        ...(mode && { mode }),
        ...(ad_account_id && { ad_account_id }),
      });
      return res?.data?.data ?? res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async handleSyncLiveForRequest({ axiosPrivate, ad_request_id }) {
    try {
      const res = await axiosPrivate.post(`${BASE}/launch/sync-live/request/`, {
        ad_request_id,
      });
      return res?.data?.data ?? res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async listSyncLiveRuns({ axiosPrivate, limit = 5 }) {
    try {
      const res = await axiosPrivate.get(`${BASE}/launch/sync-live/`, {
        params: { limit },
      });
      const list = res?.data?.data ?? [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  static async listMetaAdAccounts({ axiosPrivate }) {
    try {
      const res = await axiosPrivate.get(`${BASE}/meta/ad-accounts/`);
      const list = res?.data?.data ?? res?.data ?? [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      return [];
    }
  }

  static async listMetaCampaigns({ axiosPrivate, ad_account_id }) {
    try {
      const params = new URLSearchParams({
        ...(ad_account_id && { ad_account_id }),
      });
      const q = params.toString();
      const url = q
        ? `${BASE}/meta/campaigns/?${q}`
        : `${BASE}/meta/campaigns/`;
      const res = await axiosPrivate.get(url);
      const list = res?.data?.data ?? res?.data ?? [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      return [];
    }
  }

  static async getMetaCampaign({ axiosPrivate, campaign_id }) {
    try {
      const res = await axiosPrivate.get(
        `${BASE}/meta/campaigns/${campaign_id}/`
      );
      return res?.data?.data ?? res?.data ?? null;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      return null;
    }
  }

  static async updateMetaCampaign({ axiosPrivate, campaign_id, payload }) {
    try {
      const res = await axiosPrivate.patch(
        `${BASE}/meta/campaigns/${campaign_id}/`,
        payload
      );
      return res?.data?.data ?? res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async clearOffAdsetLimits({ axiosPrivate, campaign_id }) {
    try {
      const res = await axiosPrivate.post(
        `${BASE}/meta/campaigns/${campaign_id}/clear-off-adset-limits/`
      );
      return res?.data?.data ?? res?.data ?? { cleared: 0, skipped: 0 };
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async getLaunch({ axiosPrivate, id }) {
    try {
      const res = await axiosPrivate.get(`${BASE}/launch/${id}/`);
      return res?.data?.data ?? res?.data ?? null;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      return null;
    }
  }

  static async retryLaunch({ axiosPrivate, id }) {
    try {
      const res = await axiosPrivate.post(`${BASE}/launch/${id}/retry/`);
      return res?.data?.data ?? res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async createAdRequestSheet({ axiosPrivate, direct, agency_id }) {
    try {
      const res = await axiosPrivate.post(
        `${BASE}/ad-requests/create-sheet/`,
        direct ? { direct: true, agency_id } : {}
      );
      return res?.data?.data ?? res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async importAdRequestSheet({
    axiosPrivate,
    sheet_id,
    direct,
    agency_id,
  }) {
    try {
      const params = new URLSearchParams({
        sheet_id,
        ...(direct && { direct: "true" }),
        ...(direct && agency_id && { agency_id }),
      });
      const res = await axiosPrivate.get(
        `${BASE}/ad-requests/import-sheet/?${params.toString()}`
      );
      const data = res?.data?.data ?? res?.data ?? [];
      return Array.isArray(data) ? data : [];
    } catch (e) {
      toast.error(e?.response?.data?.error_message || e?.message);
      throw e;
    }
  }

  static async createDeliverySheet({ axiosPrivate, agency_id }) {
    const res = await axiosPrivate.post(`${BASE}/deliveries/create-sheet/`, {
      agency_id,
    });
    return res?.data?.data ?? res?.data;
  }

  static async importDeliverySheet({ axiosPrivate, sheet_id }) {
    const params = new URLSearchParams({ sheet_id });
    const res = await axiosPrivate.get(
      `${BASE}/deliveries/import-sheet/?${params.toString()}`
    );
    return res?.data?.data ?? res?.data;
  }
}

export default AdsAPIs;
